import { isDefined } from "@prismicio/editor-support/Type"
import { type RichText } from "@prismicio/types-internal/lib/customtypes"

import {
  type ExtensionPlaceholder,
  type NodeExtension,
  NodeType,
} from "../models"
import { ExtendedRichTextNodeType } from "../types"
import { default as Document } from "./Document"
import { default as HardBreak } from "./HardBreak"
import { default as ListItem } from "./ListItem"
import { default as Placeholder } from "./Placeholder"
import { SlashCommands } from "./SlashCommands"
import { default as Text } from "./Text"

export function CoreComponents(
  field: RichText,
  nodeExtensions: NodeExtension[],
) {
  const allPrismicNodeTypes = nodeExtensions
    .map((e) => e.richTextNodeTypes)
    .flat()

  const placeholderByNodeType = getPlaceHolderByNodeType(nodeExtensions)

  const placeHolderProps = {
    placeholderByNodeType,
    unfocusedPlaceholder: field.config?.placeholder,
    nodeExtensions,
  }

  const withLists =
    allPrismicNodeTypes.includes(ExtendedRichTextNodeType.orderedList) ||
    allPrismicNodeTypes.includes(ExtendedRichTextNodeType.list)

  // We want to hide the placeholders inside table because they don't look good,
  // and the pseudo-elements created by it break the layout.
  // Selection on empty cells is also broken by the extension.
  // For now, since table is only supported in TableField, disabling it like this
  // will do, but if we want to support it in for RichText fields in general, it
  // needs to be revisited, as we should not disable the whole placeholder
  // extension just because of it.
  const shouldEnablePlaceholder = !allPrismicNodeTypes.includes(
    ExtendedRichTextNodeType.table,
  )

  const richTextComponents = [
    Document.component(field.config?.single === undefined),
    Text.component,
    HardBreak.component,
    shouldEnablePlaceholder ? Placeholder(placeHolderProps) : undefined,
    withLists ? ListItem.component : undefined,
    SlashCommands.configure({ nodeTypes: allPrismicNodeTypes }),
  ]

  return richTextComponents.filter(isDefined)
}

function getPlaceHolderByNodeType(nodes: NodeExtension[]) {
  return nodes.reduce<Record<string, ExtensionPlaceholder>>((acc, node) => {
    if (node.nodeType === NodeType.Text) acc[node.name] = node.placeholder
    return acc
  }, {})
}

export { Document, HardBreak, ListItem, Text }
